import { convertHeicIfNeeded } from '../../../utils/dataProcessing';
import { applyColorMatrix, getGradientGeometry } from '../core/filters';

// Big enough for a sharp 1080-wide story, small enough that a 48 MP photo can't exhaust a phone's memory.
const MAX_PHOTO_EDGE = 2048;

const decodeImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('The photo could not be decoded'));
  image.src = url;
});

/**
 * A copy of the image no larger than `maxEdge` on its longest side.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} source
 * @param {number} maxEdge
 * @returns {HTMLCanvasElement}
 */
const scalePhoto = (source, maxEdge) => {
  const width = source.naturalWidth || source.width;
  const height = source.naturalHeight || source.height;
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
};

/**
 * Reads a photo the client picked into a canvas, upright and scaled down. The photo never leaves the device.
 *
 * @param {File} file
 * @returns {Promise<HTMLCanvasElement>}
 */
const loadPhoto = async (file) => {
  // iPhones hand over HEIC, which only Safari can decode.
  const url = URL.createObjectURL(await convertHeicIfNeeded(file));
  try {
    // Browsers apply the photo's EXIF rotation when they decode it into an image.
    return scalePhoto(await decodeImage(url), MAX_PHOTO_EDGE);
  } finally {
    URL.revokeObjectURL(url);
  }
};

const createGradient = (ctx, gradient, width, height) => {
  const geometry = getGradientGeometry(gradient, width, height);
  const canvasGradient = gradient.type === 'radial'
    ? ctx.createRadialGradient(geometry.x, geometry.y, 0, geometry.x, geometry.y, geometry.radius)
    : ctx.createLinearGradient(geometry.x0, geometry.y0, geometry.x1, geometry.y1);
  gradient.stops.forEach(([offset, color]) => canvasGradient.addColorStop(offset, color));
  return canvasGradient;
};

/**
 * A filtered copy of the photo: its color matrix, then its overlay blended on top.
 *
 * @param {HTMLCanvasElement} photo
 * @param {{ matrix: number[][], overlay: ?Object }} filter One of the filters.
 * @returns {HTMLCanvasElement}
 */
const filterPhoto = (photo, { matrix, overlay }) => {
  const canvas = document.createElement('canvas');
  canvas.width = photo.width;
  canvas.height = photo.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(photo, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  imageData.data.set(applyColorMatrix(imageData.data, matrix));
  ctx.putImageData(imageData, 0, 0);
  if (overlay) {
    ctx.globalCompositeOperation = overlay.blend;
    ctx.fillStyle = createGradient(ctx, overlay.gradient, canvas.width, canvas.height);
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  return canvas;
};

export {
  loadPhoto,
  scalePhoto,
  filterPhoto,
};
